import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Download,
  Eye,
  File,
  FileArchive,
  FileChartPie,
  FileImage,
  FileSpreadsheet,
  FileText,
  History,
  Info,
  Loader2,
  Mail,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { filesApi } from "@/api/files-api";
import { isDevS3File } from "@/lib/url-utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/lib/api-error";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GoogleDrivePicker } from "@/components/portal/google-drive-picker";
import { DocSendPicker } from "@/components/common/docsend-picker";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { failureReason, runBulkUpload } from "./bulk-upload";
import {
  describeBulkOutcome,
  describeCollapse,
  describeSharedType,
  groupFiles,
  groupKeyFor,
  queueCollisions,
  summarizeBulkOutcome,
} from "./file-groups";
import { cn, FILE_TYPE_MAPPING, fileTypeLabel } from "@/lib/utils";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useState } from "react";
import { toast } from "sonner";
import { useUser } from "@clerk/clerk-react";
import { trackFileUpload } from "@/lib/analytics/file-upload-tracking";
import { SpreadsheetViewer } from "@/components/common/spreadsheet-viewer";
import {
  BlobFileViewer,
  embedUrlFor,
  fileProxyUrl,
} from "@/components/common/file-preview-dialog";

const GoogleDriveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
      fill="#0066da"
    />
    <path
      d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
      fill="#00ac47"
    />
    <path
      d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
      fill="#ea4335"
    />
    <path
      d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
      fill="#00832d"
    />
    <path
      d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
      fill="#2684fc"
    />
    <path
      d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
      fill="#ffba00"
    />
  </svg>
);

const DocSendIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <rect width="24" height="24" rx="4" fill="#5D5FEF" />
    <path
      d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"
      fill="white"
    />
  </svg>
);

const FILE_ICONS = {
  pdf: {
    icon: FileText,
    color: "text-red-500",
    bgColor: "bg-red-100",
  },
  doc: {
    icon: File,
    color: "text-blue-500",
    bgColor: "bg-blue-100",
  },
  docx: {
    icon: File,
    color: "text-blue-500",
    bgColor: "bg-blue-100",
  },
  ppt: {
    icon: FileChartPie,
    color: "text-orange-500",
    bgColor: "bg-orange-100",
  },
  pptx: {
    icon: FileChartPie,
    color: "text-orange-500",
    bgColor: "bg-orange-100",
  },
  jpg: {
    icon: FileImage,
    color: "text-purple-500",
    bgColor: "bg-purple-100",
  },
  jpeg: {
    icon: FileImage,
    color: "text-purple-500",
    bgColor: "bg-purple-100",
  },
  png: {
    icon: FileImage,
    color: "text-purple-500",
    bgColor: "bg-purple-100",
  },
  zip: {
    icon: FileArchive,
    color: "text-yellow-500",
    bgColor: "bg-yellow-100",
  },
  csv: {
    icon: FileSpreadsheet,
    color: "text-green-500",
    bgColor: "bg-green-100",
  },
  xlsx: {
    icon: FileSpreadsheet,
    color: "text-green-500",
    bgColor: "bg-green-100",
  },
  xls: {
    icon: FileSpreadsheet,
    color: "text-green-500",
    bgColor: "bg-green-100",
  },
  ico: {
    icon: FileImage,
    color: "text-purple-500",
    bgColor: "bg-purple-100",
  },
  default: {
    icon: File,
    color: "text-gray-500",
    bgColor: "bg-gray-100",
  },
};

const formatFileSize = (size: string) => {
  return size;
};

const formatDate = (dateString: string) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toISOString().split("T")[0];
  } catch {
    return "-";
  }
};

const getFileType = (source: string) => {
  // Strip query string / fragment so signed URLs (e.g. `.xlsx?X-Amz-...`)
  // don't leak credentials into the extension we detect.
  const clean = source.split("?")[0].split("#")[0];
  const extension = clean.split(".").pop()?.toLowerCase() || "";
  return extension;
};

// Extensions the preview dialog below knows how to render — used to decide
// whether a name-derived type is usable or we should fall back to the URL.
// Keep in sync with the preview-dialog branches (isPDF / isPowerPoint /
// isWordDocument / isExcelFile / isCSVFile / isImageFile).
const KNOWN_PREVIEW_EXTENSIONS = new Set([
  "pdf",
  "ppt",
  "pptx",
  "doc",
  "docx",
  "xlsx",
  "xls",
  "csv",
  "png",
  "jpg",
  "jpeg",
  "ico",
]);

type CompanyFile = DealTransformed["company_files"][number];

const FileCardSkeleton = () => (
  <div className="flex flex-col sm:flex-row items-start sm:items-center rounded-lg border bg-card p-4 shadow-sm">
    <div className="flex flex-col sm:flex-row sm:mr-6 items-start sm:items-center gap-4 sm:gap-6 w-full">
      <Skeleton className="h-12 w-12 rounded-lg" />
      <div className="space-y-2 sm:space-y-1 w-full">
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </div>
  </div>
);

const isPDF = (fileType: string) => fileType.toLowerCase() === "pdf";

const isPowerPoint = (fileType: string) => {
  const type = fileType.toLowerCase();
  return type === "ppt" || type === "pptx";
};

const isWordDocument = (fileType: string) => {
  const type = fileType.toLowerCase();
  return type === "doc" || type === "docx";
};

const isExcelFile = (fileType: string) => {
  const type = fileType.toLowerCase();
  return type === "xlsx" || type === "xls";
};

const isCSVFile = (fileType: string) => {
  const type = fileType.toLowerCase();
  return type === "csv";
};

// Company-avatar files (e.g. the favicon written automatically by the
// website / sequence flows) often persist with no usable extension in the
// stored `file_name`. Pure extension sniffing then misses them, the preview
// dialog falls through to the generic iframe, and S3 serves the object with
// `Content-Disposition: attachment` so the browser downloads instead. The
// file-type category is the reliable signal here.
const isImageFile = (fileExtension: string, fileCategory?: string) => {
  if (fileCategory === "company avatar") return true;
  const ext = fileExtension.toLowerCase();
  return ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "ico";
};

interface ReportStatus {
  opportunityApplication?: {
    report_status?: Record<string, string>;
  };
}

export function DataRoom({
  currentDeal,
  isLoading,
  reportStatus,
}: {
  currentDeal: DealTransformed;
  isLoading: boolean;
  reportStatus?: ReportStatus;
}) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { updateDeal } = useCurrentDealStore();
  const { user } = useUser();
  const [selectedFileType, setSelectedFileType] = useState("");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [fileTypeOpen, setFileTypeOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{
    id: string;
    name: string;
    isExtracted: boolean;
  } | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  // Named after the document rather than its type, now that two documents can
  // share a type — "Showing every version of Series A Deck".
  const [historyGroupLabel, setHistoryGroupLabel] = useState("");
  const [selectedFileHistory, setSelectedFileHistory] = useState<
    Array<{
      id: string;
      name: string;
      type: string;
      // Semantic file-type category (e.g. "company avatar"), needed by
      // isImageFile() in the history-version preview branch.
      fileType?: string;
      size: string;
      addedOn: string;
      timestamp: string;
      operation: string;
      icon: React.ComponentType<{ className?: string }>;
      color: string;
      bgColor: string;
      url?: string;
      content?: string;
    }>
  >([]);

  // Bulk upload state
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkUploadFiles, setBulkUploadFiles] = useState<
    Array<{
      file: File;
      fileType: string;
      fileName: string;
      id: string;
    }>
  >([]);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  // Only surfaced once the user has tried to submit — flagging empty rows the
  // moment the dialog opens would mark every file red before they had a chance.
  const [showTypeErrors, setShowTypeErrors] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [isGoogleDriveOpen, setIsGoogleDriveOpen] = useState(false);
  const [isDocSendOpen, setIsDocSendOpen] = useState(false);
  const [bulkFileTypeOpen, setBulkFileTypeOpen] = useState<string | null>(null);

  // Check if deal has company_source - uploads are disabled for external source deals
  const hasCompanySource = Boolean(currentDeal?.overview?.companySource);

  const files =
    currentDeal?.company_files
      ?.filter((file) => !isDevS3File(file.file_url))
      ?.map((file) => {
      // Prefer the persisted filename for extension detection. `file_url`
      // may be a signed GCS/S3 URL whose query string would otherwise be
      // parsed as an extension like `xlsx?x-amz-algorithm=...`, which
      // breaks CSV/Excel detection in the preview dialog below.
      const rawFileName = file.file_name
        ? decodeURIComponent(file.file_name)
        : "";
      // Portal uploads persist file_name without an extension (e.g.
      // "pitch_deck"), so the name alone resolves to a bogus type and the
      // preview falls through to the generic iframe — which downloads the file
      // instead of previewing it. Fall back to the URL extension when the name
      // has no recognised one.
      const nameType = getFileType(rawFileName);
      const urlType = getFileType(file.file_url);
      const fileType = KNOWN_PREVIEW_EXTENSIONS.has(nameType)
        ? nameType
        : KNOWN_PREVIEW_EXTENSIONS.has(urlType)
          ? urlType
          : nameType;
      const fileConfig =
        FILE_ICONS[fileType as keyof typeof FILE_ICONS] || FILE_ICONS.default;

      const typeLabel = fileTypeLabel(file.file_type);

      const result = {
        id: file._id,
        name: rawFileName || `${typeLabel}.${fileType}`,
        typeLabel,
        type: fileType.toUpperCase(),
        size: formatFileSize(file.file_size),
        addedOn: formatDate(file.time_stamp),
        timestamp: file.time_stamp || new Date().toISOString(),
        operation: "File",
        icon: fileConfig.icon,
        color: fileConfig.color,
        bgColor: fileConfig.bgColor,
        url: file.file_url,
        fileType: file.file_type,
        uploadSource:
          file.upload_source === "google_drive"
            ? "google_drive"
            : file.upload_source === "docsend"
              ? "docsend"
              : file.upload_source === "email"
                ? "email"
                : "local",
      };

      return result;
    }) || [];

  const processedContent =
    currentDeal?.content?.extracted || currentDeal?.content?.anonymized
      ? [
        ...Object.entries(currentDeal.content.extracted || {}).map(
          ([name, content]) => {
            const label = fileTypeLabel(name);
            return {
              id: `extracted-${name}`,
              name: label,
              typeLabel: label,
              content,
              type: "TEXT",
              size: "-",
              addedOn: "-",
              timestamp: new Date().toISOString(),
              operation: "Extracted",
              icon: FileText,
              color: "text-orange-500",
              bgColor: "bg-orange-100",
              fileType: name,
            };
          }
        ),
        ...Object.entries(currentDeal.content.anonymized || {}).map(
          ([name, content]) => {
            const label = fileTypeLabel(name);
            return {
              id: `anonymized-${name}`,
              name: label,
              typeLabel: label,
              content,
              type: "TEXT",
              size: "-",
              addedOn: "-",
              timestamp: new Date().toISOString(),
              operation: "Anonymized",
              icon: FileText,
              color: "text-purple-500",
              bgColor: "bg-purple-100",
              fileType: name,
            };
          }
        ),
      ]
      : [];

  const allFiles = [...files, ...processedContent];

  // One group per document, not per document *type* — see file-groups.ts for
  // why. Three files typed "Pitch Deck" are three rows; a re-upload of the same
  // document is a version of it.
  const fileGroups = groupFiles(allFiles);
  const groupsByKey = new Map(fileGroups.map((group) => [group.key, group]));
  const latestFiles = fileGroups.map((group) => group.latest);

  // What the queued batch will do once uploaded, so the user hears it before
  // the fact rather than discovering a missing row afterwards.
  const bulkQueueWarnings = queueCollisions(bulkUploadFiles);

  const handleShowHistory = (groupKey: string) => {
    const group = groupsByKey.get(groupKey);
    if (!group) return;
    // Already newest-first out of groupFiles.
    setSelectedFileHistory(group.versions);
    setHistoryGroupLabel(group.latest.typeLabel || group.latest.name);
    setHistoryDialogOpen(true);
  };

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!selectedFileType || !files?.length) return;
    setSelectedFileName(files[0].name);
    setSelectedFile(files[0]);
  };

  const handleBulkFileSelection = (files: FileList | null) => {
    if (!files?.length) return;

    const newFiles = Array.from(files).map((file) => ({
      file,
      fileType: "",
      fileName: file.name,
      id: Math.random().toString(36).substr(2, 9),
    }));

    setBulkUploadFiles((prev) => [...prev, ...newFiles]);
  };

  const handleBulkFileTypeChange = (fileId: string, fileType: string) => {
    setBulkUploadFiles((prev) =>
      prev.map((file) => (file.id === fileId ? { ...file, fileType } : file))
    );
  };

  const handleGoogleDriveImport = (
    importedFiles: Array<
      Partial<CompanyFile> & {
        _id?: string;
        file_type?: string;
        file_url?: string;
        file_size?: string;
        file_name?: string;
        time_stamp?: string;
      }
    >
  ) => {
    if (!currentDeal || !user?.id) return;

    const driveTrackingProps = {
      upload_source: "google_drive" as const,
      context: "data_room" as const,
      workspace_id: activeWorkspaceId || "",
      opportunity_application_id: currentDeal.id,
      user_id: user.id,
    };

    const mapped: CompanyFile[] = importedFiles.map((f) => ({
      _id: String(f._id || ""),
      company: currentDeal.id,
      file_type: String(f.file_type || "google_drive"),
      file_url: String(f.file_url || ""),
      file_size: f.file_size || "-",
      file_name: f.file_name || "",
      time_stamp: f.time_stamp || new Date().toISOString(),
      upload_source: "google_drive",
    }));

    updateDeal({
      company_files: [
        ...(currentDeal.company_files || []),
        ...mapped,
      ] as CompanyFile[],
    });

    trackFileUpload({ ...driveTrackingProps, action: "started", file_count: importedFiles.length });
    importedFiles.forEach(() => {
      trackFileUpload({ ...driveTrackingProps, action: "success", file_count: 1 });
    });

    setIsGoogleDriveOpen(false);
  };

  const handleDocSendImport = (importedFile: {
    _id: string;
    file_name: string;
    file_type: string;
    file_url: string;
    file_size: string;
  }) => {
    if (!currentDeal || !user?.id) return;

    const newFile: CompanyFile = {
      _id: importedFile._id,
      company: currentDeal.id,
      file_type: importedFile.file_type || "pdf",
      file_url: importedFile.file_url,
      file_size: importedFile.file_size,
      file_name: importedFile.file_name,
      time_stamp: new Date().toISOString(),
      upload_source: "docsend",
    };

    updateDeal({
      company_files: [
        ...(currentDeal.company_files || []),
        newFile,
      ] as CompanyFile[],
    });

    const props = {
      upload_source: "docsend" as const,
      context: "data_room" as const,
      file_count: 1,
      workspace_id: activeWorkspaceId || "",
      opportunity_application_id: currentDeal.id,
      user_id: user.id,
    };

    trackFileUpload({ ...props, action: "started" });
    trackFileUpload({ ...props, action: "success" });

    setIsDocSendOpen(false);
  };

  const handleDocSendError = () => {
    if (!currentDeal || !user?.id) return;
    trackFileUpload({
      action: "failed",
      upload_source: "docsend",
      context: "data_room",
      file_count: 1,
      workspace_id: activeWorkspaceId || "",
      opportunity_application_id: currentDeal.id,
      user_id: user.id,
      failure_reason: "server_error",
    });
  };

  const handleGoogleDriveError = (failedCount: number) => {
    if (!currentDeal || !user?.id) return;
    for (let i = 0; i < failedCount; i++) {
      trackFileUpload({
        action: "failed",
        upload_source: "google_drive",
        context: "data_room",
        file_count: 1,
        workspace_id: activeWorkspaceId || "",
        opportunity_application_id: currentDeal.id,
        user_id: user.id,
        failure_reason: "server_error",
      });
    }
  };

  const removeBulkFile = (fileId: string) => {
    setBulkUploadFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedFileType || !user?.id) return;

    setIsUploading(true);
    setUploadProgress(0);

    const trackingProps = {
      upload_source: "local" as const,
      context: "data_room" as const,
      workspace_id: activeWorkspaceId || "",
      opportunity_application_id: currentDeal.id,
      user_id: user.id,
    };

    trackFileUpload({ ...trackingProps, action: "started", file_count: 1 });

    try {
      const formData = {
        file: selectedFile,
        file_name: selectedFileName,
        file_type: selectedFileType,
        object_ref: "company",
        object_ref_value: currentDeal.id,
      };

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 5;
        });
      }, 100);

      const response = await filesApi.uploadFile(
        formData,
        currentDeal.id,
        activeWorkspaceId || ""
      );

      // Pass only the key we changed, read off fresh store state — handing
      // `updateDeal` a whole spread of the closure's `currentDeal` reverts
      // anything else edited while the upload was in flight.
      updateDeal({
        company_files: [
          ...(useCurrentDealStore.getState().currentDeal?.company_files || []),
          {
            ...response.result,
            _id: response.result._id,
            file_name: selectedFileName,
            company: currentDeal.id,
            time_stamp: new Date().toISOString(),
          },
        ] as CompanyFile[],
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      trackFileUpload({ ...trackingProps, action: "success", file_count: 1 });
      toast.success("File uploaded successfully");

      // Reset form and close dialog
      setSelectedFile(null);
      setSelectedFileName("");
      setSelectedFileType("");
      setDialogOpen(false);
    } catch (error) {
      console.error("Upload error:", error);
      const isNetwork = error instanceof Error && error.message.includes("Network Error");
      trackFileUpload({ ...trackingProps, action: "failed", file_count: 1, failure_reason: isNetwork ? "network_error" : "server_error" });
      toast.error(getErrorMessage(error, "Failed to upload file"));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkUploadFiles.length === 0 || !user?.id) return;

    // Flag the offending rows rather than only saying "some row is wrong" —
    // the old blanket toast left the user hunting down the list.
    const invalidFiles = bulkUploadFiles.filter((file) => !file.fileType);
    if (invalidFiles.length > 0) {
      setShowTypeErrors(true);
      toast.error(
        `Select a document type for ${invalidFiles.length} ${invalidFiles.length === 1 ? "file" : "files"}`
      );
      return;
    }

    // Snapshot which documents already exist so the outcome can distinguish a
    // new document from a new version of one.
    const existingKeys = new Set(fileGroups.map((group) => group.key));

    setIsBulkUploading(true);
    setBulkUploadProgress(0);
    setCurrentUploadIndex(0);

    const bulkTrackingProps = {
      upload_source: "bulk" as const,
      context: "data_room" as const,
      workspace_id: activeWorkspaceId || "",
      opportunity_application_id: currentDeal.id,
      user_id: user.id,
    };

    trackFileUpload({ ...bulkTrackingProps, action: "started", file_count: bulkUploadFiles.length });

    try {
      const { uploaded, failed } = await runBulkUpload({
        items: bulkUploadFiles,

        upload: (fileItem) =>
          filesApi.uploadFile(
            {
              file: fileItem.file,
              file_name: fileItem.fileName,
              file_type: fileItem.fileType,
              object_ref: "company",
              object_ref_value: currentDeal.id,
            },
            currentDeal.id,
            activeWorkspaceId || ""
          ),

        // Merge each file as it lands, against the store's *current* value
        // rather than the `currentDeal` captured in this closure. A bulk
        // upload runs for as long as it takes to push N files, and anything
        // else that edits the deal in that window would be reverted if we
        // wrote back a pre-loop snapshot. `getState()` is the non-reactive
        // read; `updateDeal` shallow-merges what we hand it.
        onUploaded: (response, fileItem) => {
          const existing =
            useCurrentDealStore.getState().currentDeal?.company_files || [];

          updateDeal({
            company_files: [
              ...existing,
              {
                ...response.result,
                _id: response.result._id,
                // Keep the local name authoritative: it is what the user
                // actually picked, and it stays correct against a backend
                // that predates the file_name fix.
                file_name: fileItem.fileName,
                company: currentDeal.id,
                time_stamp: new Date().toISOString(),
              },
            ] as CompanyFile[],
          });

          trackFileUpload({ ...bulkTrackingProps, action: "success", file_count: 1 });
        },

        onProgress: ({ index }) => setCurrentUploadIndex(index),

        onSettled: ({ uploadedCount, total }) =>
          setBulkUploadProgress((uploadedCount / total) * 100),
      });

      for (const { item, error } of failed) {
        console.error(`Error uploading ${item.fileName}:`, error);
        trackFileUpload({
          ...bulkTrackingProps,
          action: "failed",
          file_count: 1,
          failure_reason: failureReason(error),
        });
      }

      // Say what actually landed. "Uploaded 3 files" alone was what let three
      // files collapse into one row without anyone noticing.
      const outcome = describeBulkOutcome(
        summarizeBulkOutcome({ uploaded, existingKeys })
      );

      if (failed.length === 0) {
        toast.success(outcome);
        setBulkUploadDialogOpen(false);
        setBulkUploadFiles([]);
        return;
      }

      // Partial or total failure: say so, and keep the dialog open with only
      // the files that failed still queued, so they can be retried without
      // being dropped back in by hand. Previously this reported success
      // ("Successfully uploaded 0 files") and discarded them.
      if (uploaded.length > 0) toast.success(outcome);
      toast.error(
        `Failed to upload: ${failed.map(({ item }) => item.fileName).join(", ")}. Please try again.`
      );
      setBulkUploadFiles(failed.map(({ item }) => item));
    } catch (error) {
      console.error("Bulk upload error:", error);
      toast.error(getErrorMessage(error, "Failed to complete bulk upload"));
    } finally {
      setIsBulkUploading(false);
      setBulkUploadProgress(0);
      setCurrentUploadIndex(0);
    }
  };

  const handleBulkUploadDialogClose = () => {
    setBulkUploadDialogOpen(false);
    setBulkUploadFiles([]);
    setIsBulkUploading(false);
    setBulkUploadProgress(0);
    setCurrentUploadIndex(0);
    setShowTypeErrors(false);
  };

  const handleDelete = async (fileId: string, isExtracted: boolean = false) => {
    try {
      if (isExtracted) {
        // For extracted/anonymized content, fileId will be in format "extracted-{type}" or "anonymized-{type}"
        const [operation, fileType] = fileId.split("-");
        await filesApi.deleteExtractedFile(fileType, currentDeal.id);

        // Remove from extracted/anonymized content. Rebuild from fresh store
        // state and send only `content` — spreading the whole closure deal
        // back reverts unrelated fields changed since this render.
        const fresh = useCurrentDealStore.getState().currentDeal;
        const content = {
          ...fresh?.content,
          extracted: { ...(fresh?.content?.extracted || {}) },
          anonymized: { ...(fresh?.content?.anonymized || {}) },
        };

        // Delete from the appropriate content type
        if (operation === "extracted") {
          delete content.extracted[fileType];
        }
        if (operation === "anonymized") {
          delete content.anonymized[fileType];
        }

        updateDeal({ content });
      } else {
        const fileToDelete = currentDeal.company_files?.find(
          (file) => file._id === fileId
        );

        if (!fileToDelete) {
          throw new Error("File not found");
        }

        // Pass the file's own id: several files can share a file_type (the
        // data room shows them as versions), and without it the backend
        // deletes whichever one it happens to match first.
        await filesApi.deleteFile(
          fileToDelete.file_type,
          currentDeal.id,
          activeWorkspaceId || "",
          fileId
        );

        updateDeal({
          company_files: (
            useCurrentDealStore.getState().currentDeal?.company_files || []
          ).filter((file) => file._id !== fileId),
        });
      }

      toast.success("File deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(getErrorMessage(error, "Failed to delete file"));
    }
  };

  const handleDeleteClick = (
    id: string,
    name: string,
    isExtracted: boolean
  ) => {
    setFileToDelete({ id, name, isExtracted });
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    await handleDelete(fileToDelete.id, fileToDelete.isExtracted);
    setFileToDelete(null);
  };

  const getFileStatus = (fileType: string) => {
    if (!reportStatus?.opportunityApplication?.report_status) return null;

    // Get the mapped file type from FILE_TYPE_MAPPING
    const mappedType = Object.entries(FILE_TYPE_MAPPING).find(
      ([key]) => key.toLowerCase() === fileType.toLowerCase()
    )?.[1];

    // If no mapping found, use the original type
    const typeToCheck = mappedType || fileType;

    // Use the type as is since report status uses spaces
    const status =
      reportStatus.opportunityApplication.report_status[typeToCheck];

    return status;
  };

  const renderStatusIndicator = (fileType: string) => {
    const status = getFileStatus(fileType);
    if (!status) return null;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            {status === "completed" ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : status === "failed" ? (
              <AlertCircle className="h-4 w-4 text-red-500" />
            ) : status === "in_progress" ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            ) : null}
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {status === "completed"
                ? "Processing completed"
                : status === "failed"
                  ? "Processing failed"
                  : status === "in_progress"
                    ? "Processing in progress"
                    : ""}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div>
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-serif text-3xl sm:text-4xl text-foreground">
                Data Room
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                Access and manage company documents
              </p>
            </div>

            {/* Add File button - primary action */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <DialogTrigger asChild>
                        <Button disabled={hasCompanySource} className="gap-2">
                          <Plus className="h-4 w-4" />
                          Add File
                        </Button>
                      </DialogTrigger>
                    </span>
                  </TooltipTrigger>
                  {hasCompanySource && (
                    <TooltipContent>
                      <p>
                        Cannot add documents because this deal is added from
                        Deal Radar
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <DialogContent>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label>File Type</Label>
                    <Popover open={fileTypeOpen} onOpenChange={setFileTypeOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={fileTypeOpen}
                          className="w-full justify-between font-normal"
                        >
                          {selectedFileType
                            ? Object.entries(FILE_TYPE_MAPPING).find(
                                ([, value]) => value === selectedFileType
                              )?.[0] || "Select file type"
                            : "Select file type"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search file type..." />
                          <CommandList>
                            <CommandEmpty>No file type found.</CommandEmpty>
                            <CommandGroup>
                              {Object.entries(FILE_TYPE_MAPPING).map(([label, value]) => (
                                <CommandItem
                                  key={label}
                                  value={label}
                                  onSelect={() => {
                                    setSelectedFileType(value);
                                    setFileTypeOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedFileType === value ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>Operation</Label>
                    <RadioGroup
                      onValueChange={setSelectedFileType}
                      defaultValue="upload"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="upload" id="upload" />
                        <Label htmlFor="upload">Upload file</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center border-primary"
                    onDrop={(e) => {
                      e.preventDefault();
                      if (selectedFileType) {
                        handleFileSelection(e.dataTransfer.files);
                      }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      onChange={(e) => handleFileSelection(e.target.files)}
                      disabled={!selectedFileType}
                    />
                    <label
                      htmlFor="file-upload"
                      className={`flex flex-col items-center ${selectedFileType
                        ? "cursor-pointer"
                        : "cursor-not-allowed"
                        }`}
                    >
                      <Upload
                        className={`h-8 w-8 mb-2 ${selectedFileType
                          ? "text-primary"
                          : "text-muted-foreground"
                          }`}
                      />
                      {selectedFileName ? (
                        <span className="text-sm text-primary font-medium truncate max-w-[200px]">
                          {selectedFileName}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {selectedFileType
                            ? "Drag and drop or click to upload"
                            : "Select a file type to upload"}
                        </span>
                      )}
                    </label>
                  </div>

                  {selectedFileType && (
                    <div className="space-y-4 mt-4">
                      {isUploading && (
                        <div className="space-y-2">
                          <Progress value={uploadProgress} />
                          <p className="text-sm text-center text-muted-foreground">
                            Uploading... {uploadProgress}%
                          </p>
                        </div>
                      )}
                      <Button
                        className="w-full"
                        onClick={handleUpload}
                        disabled={isUploading || !selectedFileName}
                      >
                        {isUploading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        {isUploading ? "Uploading..." : "Upload File"}
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Import options - secondary actions */}
          <div className="flex flex-wrap items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => setIsGoogleDriveOpen(true)}
                      disabled={hasCompanySource}
                    >
                      <GoogleDriveIcon className="h-4 w-4" />
                      Import from Google Drive
                    </Button>
                  </span>
                </TooltipTrigger>
                {hasCompanySource && (
                  <TooltipContent>
                    <p>
                      Cannot add documents because this deal is added from Deal Radar
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={() => setIsDocSendOpen(true)}
                      disabled={hasCompanySource}
                    >
                      <DocSendIcon className="h-4 w-4" />
                      Import from DocSend
                    </Button>
                  </span>
                </TooltipTrigger>
                {hasCompanySource && (
                  <TooltipContent>
                    <p>
                      Cannot add documents because this deal is added from Deal Radar
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <Dialog
              open={bulkUploadDialogOpen}
              onOpenChange={setBulkUploadDialogOpen}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <DialogTrigger asChild>
                        <Button variant="outline" disabled={hasCompanySource}>
                          <Upload className="h-4 w-4 mr-2" />
                          Bulk Upload
                        </Button>
                      </DialogTrigger>
                    </span>
                  </TooltipTrigger>
                  {hasCompanySource && (
                    <TooltipContent>
                      <p>
                        Cannot add documents because this deal is added from
                        Deal Radar
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <DialogContent className="max-h-[90vh] w-[95vw] sm:w-[90vw] md:w-[700px] max-w-[700px] z-50 overflow-x-hidden">
                <div className="space-y-4 min-w-0">
                  <div>
                    <h3 className="text-lg font-semibold">Bulk File Upload</h3>
                    <p className="text-sm text-muted-foreground">
                      Upload multiple files at once. Select file types for each
                      file.
                    </p>
                  </div>

                  {/* File Drop Zone */}
                  <div
                    className="border-2 border-dashed rounded-lg p-4 sm:p-6 text-center border-primary hover:border-primary/70 transition-colors"
                    onDrop={(e) => {
                      e.preventDefault();
                      handleBulkFileSelection(e.dataTransfer.files);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <input
                      type="file"
                      multiple
                      id="bulk-file-upload"
                      className="hidden"
                      onChange={(e) => handleBulkFileSelection(e.target.files)}
                    />
                    <label
                      htmlFor="bulk-file-upload"
                      className="cursor-pointer"
                    >
                      <Upload className="mx-auto h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground mb-2" />
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Drag and drop files here, or click to browse
                      </p>
                    </label>
                  </div>

                  {/* Selected Files List */}
                  {bulkUploadFiles.length > 0 && (
                    <div className="rounded-md border max-h-[35vh] sm:max-h-[40vh] overflow-y-auto">
                      <div className="divide-y">
                        {bulkUploadFiles.map((fileItem) => (
                          <div
                            key={fileItem.id}
                            className="p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-xs sm:text-sm truncate">
                                {fileItem.fileName}
                              </p>
                              {showTypeErrors && !fileItem.fileType && (
                                <p className="text-[11px] text-destructive">
                                  Document type required
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
                              <Popover
                                open={bulkFileTypeOpen === fileItem.id}
                                onOpenChange={(open) => setBulkFileTypeOpen(open ? fileItem.id : null)}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-invalid={showTypeErrors && !fileItem.fileType}
                                    className={cn(
                                      "w-[130px] sm:w-[160px] h-8 text-xs sm:text-sm flex-shrink-0 justify-between font-normal",
                                      // Recomputed per render, so picking a type
                                      // clears this row's flag without any extra
                                      // state to keep in sync.
                                      showTypeErrors &&
                                        !fileItem.fileType &&
                                        "border-destructive text-destructive"
                                    )}
                                  >
                                    <span className="truncate">
                                      {fileItem.fileType
                                        ? Object.entries(FILE_TYPE_MAPPING).find(
                                            ([, value]) => value === fileItem.fileType
                                          )?.[0] || "Type"
                                        : "Type"}
                                    </span>
                                    <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[200px] p-0 z-[200]" align="start">
                                  <Command>
                                    <CommandInput placeholder="Search..." className="h-8" />
                                    <CommandList>
                                      <CommandEmpty>No type found.</CommandEmpty>
                                      <CommandGroup>
                                        {Object.entries(FILE_TYPE_MAPPING).map(([label, value]) => (
                                          <CommandItem
                                            key={value}
                                            value={label}
                                            onSelect={() => {
                                              handleBulkFileTypeChange(fileItem.id, value);
                                              setBulkFileTypeOpen(null);
                                            }}
                                            className="text-xs sm:text-sm"
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-3 w-3",
                                                fileItem.fileType === value ? "opacity-100" : "opacity-0"
                                              )}
                                            />
                                            {label}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeBulkFile(fileItem.id)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Progress */}
                  {isBulkUploading && (
                    <div className="space-y-2">
                      <Progress value={bulkUploadProgress} />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>
                          Uploading {currentUploadIndex + 1} of{" "}
                          {bulkUploadFiles.length} files
                        </span>
                        <span>{Math.round(bulkUploadProgress)}%</span>
                      </div>
                      {bulkUploadFiles[currentUploadIndex] && (
                        <p className="text-xs text-muted-foreground">
                          Currently uploading:{" "}
                          {bulkUploadFiles[currentUploadIndex].fileName}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Two separate notices, because after the grouping fix they
                      mean different things. A collapse really does hide files
                      behind one row; a shared type does not, but still matters
                      because AI ingestion keys on (company, file_type). One
                      combined warning would be false in both directions. */}
                  {!isBulkUploading && bulkQueueWarnings.collapses.length > 0 && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                      <div className="space-y-1">
                        {bulkQueueWarnings.collapses.map((entry) => (
                          <p key={`${entry.label}-${entry.fileName}`} className="text-amber-800">
                            {describeCollapse(entry)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {!isBulkUploading && bulkQueueWarnings.sharedTypes.length > 0 && (
                    <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
                      <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      <div className="space-y-1">
                        {bulkQueueWarnings.sharedTypes.map((entry) => (
                          <p key={entry.label} className="text-muted-foreground">
                            {describeSharedType(entry)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2 min-w-0">
                    <Button
                      variant="outline"
                      onClick={handleBulkUploadDialogClose}
                      disabled={isBulkUploading}
                      className="w-full sm:w-auto flex-shrink-0"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleBulkUpload}
                      disabled={isBulkUploading || bulkUploadFiles.length === 0}
                      className="w-full sm:w-auto flex-shrink-0 min-w-0"
                    >
                      {isBulkUploading ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                          <span className="truncate">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <Upload className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">
                            Upload {bulkUploadFiles.length} Files
                          </span>
                        </div>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-border/60">
            <FileCardSkeleton />
            <FileCardSkeleton />
            <FileCardSkeleton />
          </div>
        ) : latestFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 py-12 text-center">
            <p className="text-sm text-foreground">No files yet</p>
            <p className="text-xs text-muted-foreground">
              Upload pitch decks, financials, and other materials to populate
              the data room.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {latestFiles.map((file) => {
              const Icon = file.icon;
              // Still the *document type*, not the group key:
              // renderStatusIndicator maps it through FILE_TYPE_MAPPING to find
              // the processing state, and a composite key silently resolves to
              // no status at all.
              const fileType =
                file.fileType || file.name.split(".")[0].toLowerCase();
              const groupKey = groupKeyFor(file);
              const versions = groupsByKey.get(groupKey)?.versions ?? [file];
              const hasMultipleVersions = versions.length > 1;
              const uploadSource =
                "uploadSource" in file ? file.uploadSource : undefined;
              // "extracted" / "anonymized" content rows don't carry a url
              // — narrow with the `in` operator so TS is happy and we
              // skip the avatar-image branch for those rows automatically.
              const fileUrl =
                "url" in file && typeof file.url === "string"
                  ? file.url
                  : undefined;
              const isAvatarImage =
                file.fileType === "company avatar" &&
                !!fileUrl &&
                /\.(jpg|jpeg|png|gif|webp|svg|ico)$/i.test(fileUrl);

              return (
                <li
                  key={file.id}
                  className="group relative flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 px-4 py-3 transition-colors hover:bg-muted/30"
                >
                  <div className="flex flex-1 min-w-0 items-center gap-3">
                    {/* Compact icon tile — small, muted, consistent across types
                       instead of per-file coloured blocks. Company avatar /
                       logo files swap the placeholder icon for the actual
                       image when the URL points to a renderable asset; if the
                       image 404s onError swaps back to the placeholder. */}
                    {isAvatarImage ? (
                      <img
                        src={fileUrl}
                        alt={file.typeLabel}
                        className="h-9 w-9 shrink-0 rounded-md border border-border object-contain bg-card"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = "none";
                          target.nextElementSibling?.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    <div
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground",
                        isAvatarImage && "hidden"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <h3 className="truncate text-sm font-medium text-foreground">
                          {file.typeLabel}
                        </h3>
                        {renderStatusIndicator(fileType)}
                      </div>
                      {file.name && file.name !== file.typeLabel && (
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                          {file.name}
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                        <Badge
                          variant="secondary"
                          className="font-medium text-[10px] px-1.5 py-0 h-4"
                        >
                          {file.operation}
                        </Badge>
                        <span className="uppercase tracking-wide">{file.type}</span>
                        <span className="text-muted-foreground/50">·</span>
                        <span className="tabular-nums">{file.size}</span>
                        <span className="text-muted-foreground/50">·</span>
                        <span>{file.addedOn}</span>
                        {uploadSource && (
                          <>
                            <span className="text-muted-foreground/50">·</span>
                            <span className="inline-flex items-center gap-1">
                              {uploadSource === "google_drive" ? (
                                <>
                                  <GoogleDriveIcon className="h-3 w-3" />
                                  <span>Google Drive</span>
                                </>
                              ) : uploadSource === "docsend" ? (
                                <>
                                  <DocSendIcon className="h-3 w-3" />
                                  <span>DocSend</span>
                                </>
                              ) : uploadSource === "email" ? (
                                <>
                                  <Mail className="h-3 w-3" />
                                  <span>Email</span>
                                </>
                              ) : (
                                <>
                                  <Upload className="h-3 w-3" />
                                  <span>Local</span>
                                </>
                              )}
                            </span>
                          </>
                        )}
                        {hasMultipleVersions && (
                          <>
                            <span className="text-muted-foreground/50">·</span>
                            {/* The badge is the affordance. It used to be inert
                                text beside a History icon that only appeared on
                                hover, which is why nobody found the older
                                versions and assumed the upload had dropped
                                them. */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowHistory(groupKey);
                              }}
                              aria-label={`View ${versions.length} versions of ${file.typeLabel || file.name}`}
                              className="inline-flex h-4 items-center gap-1 rounded-full border border-border/70 px-1.5 text-[10px] tabular-nums transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            >
                              <History className="h-2.5 w-2.5" />
                              {versions.length} versions
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                {"content" in file && (
                  <div className="flex gap-2 mt-4 sm:mt-0 sm:ml-auto opacity-100 sm:opacity-0 transition-opacity group-hover:opacity-100">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <div className="mt-2 text-sm max-h-[60vh] overflow-y-auto">
                          {file.content}
                        </div>
                      </DialogContent>
                    </Dialog>
                    {/* The hover-only History button that used to sit here is
                        gone — the always-visible versions badge above does the
                        same job and can actually be found. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8"
                      onClick={() =>
                        handleDeleteClick(
                          file.id,
                          file.name,
                          file.operation === "Extracted" ||
                          file.operation === "Anonymized"
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {!("content" in file) && (
                  <div className="flex gap-2 mt-4 sm:mt-0 sm:ml-auto opacity-100 sm:opacity-0 transition-opacity group-hover:opacity-100">
                    {!isDevS3File(file.url) && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl h-[80vh] overflow-hidden">
                          <div className="relative w-full h-full overflow-hidden">
                            {isPDF(file.type) ? (
                              // Read through our proxy and rendered from a
                              // blob. Files ingested from email are stored
                              // with an attachment disposition, which an
                              // embedded viewer cannot display.
                              <BlobFileViewer
                                src={fileProxyUrl(file.id)}
                                fileName={file.name}
                                mimeType="application/pdf"
                                kind="document"
                              />
                            ) : isPowerPoint(file.type) ? (
                              <>
                                <div className="absolute inset-0 flex items-center justify-center bg-white">
                                  <Loader2 className="h-8 w-8 animate-spin" />
                                </div>
                                <iframe
                                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                    embedUrlFor(file.url!, file.id)
                                  )}`}
                                  className="w-full h-full"
                                  title={file.name}
                                  onLoad={(e) => {
                                    const iframe = e.target as HTMLIFrameElement;
                                    const loader =
                                      iframe.previousSibling as HTMLDivElement;
                                    if (loader) loader.style.display = "none";
                                  }}
                                />
                              </>
                            ) : isWordDocument(file.type) ? (
                              <>
                                <div className="absolute inset-0 flex items-center justify-center bg-white">
                                  <Loader2 className="h-8 w-8 animate-spin" />
                                </div>
                                <iframe
                                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                                    embedUrlFor(file.url!, file.id)
                                  )}&embedded=true`}
                                  className="w-full h-full"
                                  title={file.name}
                                  onLoad={(e) => {
                                    const iframe = e.target as HTMLIFrameElement;
                                    const loader =
                                      iframe.previousSibling as HTMLDivElement;
                                    if (loader) loader.style.display = "none";
                                  }}
                                />
                              </>
                            ) : isCSVFile(file.type) || isExcelFile(file.type) ? (
                              <SpreadsheetViewer
                                url={file.url!}
                                fileName={file.name}
                                fileId={file.id}
                              />
                            ) : isImageFile(file.type, file.fileType) ? (
                              <div className="relative flex items-center justify-center w-full h-full p-4 overflow-hidden">
                                <img
                                  src={file.url}
                                  alt={file.name}
                                  className="w-full h-full object-contain shadow-sm rounded-md"
                                  onError={(e) => {
                                    const target = e.currentTarget;
                                    // For ICO files, try Google's favicon service
                                    if (
                                      file.type.toLowerCase() === "ico" &&
                                      currentDeal?.overview?.website
                                    ) {
                                      try {
                                        const url =
                                          currentDeal.overview.website;
                                        const domain = new URL(
                                          url.startsWith("http")
                                            ? url
                                            : `https://${url}`
                                        ).hostname;
                                        target.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
                                        target.onerror = () => {
                                          target.style.display = "none";
                                          const fallback =
                                            target.nextElementSibling as HTMLElement;
                                          if (fallback)
                                            fallback.style.display = "flex";
                                        };
                                        return;
                                      } catch {
                                        // fall through to default fallback
                                      }
                                    }
                                    target.style.display = "none";
                                    const fallback =
                                      target.nextElementSibling as HTMLElement;
                                    if (fallback)
                                      fallback.style.display = "flex";
                                  }}
                                />
                                <div
                                  className="flex-col items-center justify-center gap-2 text-gray-400"
                                  style={{ display: "none" }}
                                >
                                  <FileImage className="w-16 h-16" />
                                  <p className="text-sm">
                                    Image preview not available
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <iframe
                                src={file.url}
                                className="w-full h-full"
                                title={file.name}
                              />
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    {/* Second hover-only History button removed — see the
                        versions badge above. */}
                    {!isDevS3File(file.url) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8"
                        onClick={() => handleDownload(file.url!, file.name)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8"
                      onClick={() =>
                        handleDeleteClick(
                          file.id,
                          file.name,
                          file.operation === "Extracted" ||
                          file.operation === "Anonymized"
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
          </ul>
        )}
      </div>

      <AlertDialog
        open={!!fileToDelete}
        onOpenChange={() => setFileToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{fileToDelete?.name}". This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* File History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[80vh] overflow-hidden p-4 sm:p-6">
          <div className="space-y-4 overflow-hidden">
            <div>
              <h3 className="text-lg font-semibold">File History</h3>
              <p className="text-sm text-muted-foreground">
                Showing every version of
                {historyGroupLabel ? ` "${historyGroupLabel}"` : " this document"},
                newest to oldest
              </p>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {selectedFileHistory.map((file, index) => {
                const Icon = file.icon;
                const isLatest = index === 0;
                // Match the main file-list behaviour: render the actual
                // company logo image for "company avatar" entries when the
                // URL points to a renderable asset, with the placeholder
                // tile as the onError fallback.
                const isAvatarImage =
                  file.fileType === "company avatar" &&
                  !!file.url &&
                  /\.(jpg|jpeg|png|gif|webp|svg|ico)$/i.test(file.url);

                return (
                  <div
                    key={file.id}
                    className={`flex items-center gap-4 p-3 rounded-lg border ${isLatest ? "bg-primary/5 border-primary/20" : "bg-card"
                      }`}
                  >
                    {isAvatarImage ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        className="h-10 w-10 shrink-0 rounded-lg border border-border object-contain bg-card"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = "none";
                          target.nextElementSibling?.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    <div
                      className={cn(
                        "shrink-0 rounded-lg p-2",
                        file.bgColor,
                        isAvatarImage && "hidden"
                      )}
                    >
                      <Icon className={`h-5 w-5 ${file.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="font-medium truncate">{file.name}</h4>
                        {isLatest && (
                          <Badge variant="default" className="text-xs">
                            Latest
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
                        <span>{file.operation}</span>
                        <span>•</span>
                        <span>{file.type}</span>
                        <span>•</span>
                        <span>{file.size}</span>
                        <span>•</span>
                        <span>{file.addedOn}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {"content" in file ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl">
                            <div className="mt-2 text-sm max-h-[60vh] overflow-y-auto">
                              {file.content}
                            </div>
                          </DialogContent>
                        </Dialog>
                      ) : !isDevS3File(file.url) ? (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl h-[80vh]">
                            <div className="relative w-full h-full p-2">
                              {isPDF(file.type) ? (
                                <BlobFileViewer
                                  src={fileProxyUrl(file.id)}
                                  fileName={file.name}
                                  mimeType="application/pdf"
                                  kind="document"
                                />
                              ) : isPowerPoint(file.type) ? (
                                <>
                                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                                    <Loader2 className="h-8 w-8 animate-spin" />
                                  </div>
                                  <iframe
                                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                                      embedUrlFor(file.url!, file.id)
                                    )}`}
                                    className="w-full h-full"
                                    title={file.name}
                                    onLoad={(e) => {
                                      const iframe =
                                        e.target as HTMLIFrameElement;
                                      const loader =
                                        iframe.previousSibling as HTMLDivElement;
                                      if (loader) loader.style.display = "none";
                                    }}
                                  />
                                </>
                              ) : isWordDocument(file.type) ? (
                                <>
                                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                                    <Loader2 className="h-8 w-8 animate-spin" />
                                  </div>
                                  <iframe
                                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                                      embedUrlFor(file.url!, file.id)
                                    )}&embedded=true`}
                                    className="w-full h-full"
                                    title={file.name}
                                    onLoad={(e) => {
                                      const iframe =
                                        e.target as HTMLIFrameElement;
                                      const loader =
                                        iframe.previousSibling as HTMLDivElement;
                                      if (loader) loader.style.display = "none";
                                    }}
                                  />
                                </>
                              ) : isCSVFile(file.type) ||
                                isExcelFile(file.type) ? (
                                <SpreadsheetViewer
                                  url={file.url!}
                                  fileName={file.name}
                                  fileId={file.id}
                                />
                              ) : isImageFile(file.type, file.fileType) ? (
                                <div className="flex items-center justify-center w-full h-full overflow-hidden p-2">
                                  <img
                                    src={file.url}
                                    alt={file.name}
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => {
                                      const target = e.currentTarget;
                                      if (
                                        file.type.toLowerCase() === "ico" &&
                                        currentDeal?.overview?.website
                                      ) {
                                        try {
                                          const url =
                                            currentDeal.overview.website;
                                          const domain = new URL(
                                            url.startsWith("http")
                                              ? url
                                              : `https://${url}`
                                          ).hostname;
                                          target.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
                                          target.onerror = null;
                                          return;
                                        } catch {
                                          // ignore
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              ) : (
                                <iframe
                                  src={file.url}
                                  className="w-full h-full"
                                  title={file.name}
                                />
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      ) : null}
                      {!("content" in file) && !isDevS3File(file.url) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8"
                          onClick={() => handleDownload(file.url!, file.name)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8"
                        onClick={() =>
                          handleDeleteClick(
                            file.id,
                            file.name,
                            file.operation === "Extracted" ||
                            file.operation === "Anonymized"
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GoogleDrivePicker
        open={isGoogleDriveOpen}
        onClose={() => setIsGoogleDriveOpen(false)}
        companyId={currentDeal?.id || ""}
        opportunityId={activeWorkspaceId || ""}
        onImportComplete={handleGoogleDriveImport}
        onImportError={handleGoogleDriveError}
      />

      <DocSendPicker
        open={isDocSendOpen}
        onClose={() => setIsDocSendOpen(false)}
        companyId={currentDeal?.id || ""}
        opportunityId={activeWorkspaceId || ""}
        mode="import"
        onImportComplete={handleDocSendImport}
        onImportError={handleDocSendError}
      />
    </div>
  );
}
